import { stripHtmlFromErrorMessage } from '@/utilities/axios/errorUtils'

const IGNORED_LOC_PREFIXES = new Set(['body', 'query', 'path'])

/** Patient-facing names for API / schema field keys. Unmapped keys are omitted from UI copy. */
export const VALIDATION_FIELD_LABELS: Record<string, string> = {
  first_name: 'first name',
  last_name: 'last name',
  phone: 'phone number',
  address1: 'address',
  address2: 'address line 2',
  city: 'city',
  state: 'state',
  zipcode: 'zip code',
  dob: 'date of birth',
  sex_at_birth: 'sex assigned at birth',
  sex_assigned_at_birth: 'sex assigned at birth',
  email: 'email',
  login: 'email',
  pcp_name: 'PCP name',
  pcp_fax: 'PCP fax',
  pcp_address1: 'PCP street address',
  pcp_address2: 'PCP address line 2',
  pcp_city: 'PCP city',
  pcp_state: 'PCP state',
  pcp_zip: 'PCP zip code',
  default_pcp: 'PCP information',
}

export type HttpValidationDetailItem = {
  loc?: Array<string | number>
  msg?: unknown
}

export function labelForValidationFieldPath(path: string): string | undefined {
  if (VALIDATION_FIELD_LABELS[path]) {
    return VALIDATION_FIELD_LABELS[path]
  }
  const leaf = path.split('.').pop()
  return leaf ? VALIDATION_FIELD_LABELS[leaf] : undefined
}

export function formatValidationDetailPath(loc: Array<string | number> | undefined): string {
  if (!loc?.length) {
    return ''
  }
  const fieldParts = IGNORED_LOC_PREFIXES.has(String(loc[0])) ? loc.slice(1) : loc
  const labels = fieldParts
    .map((part) => (typeof part === 'string' ? VALIDATION_FIELD_LABELS[part] : undefined))
    .filter((label): label is string => Boolean(label))
  return labels.at(-1) ?? ''
}

function messageFromDetailItem(
  item: unknown,
  options?: { includeFieldLabels?: boolean },
): string | undefined {
  if (typeof item === 'string') {
    const stripped = stripHtmlFromErrorMessage(item).trim()
    return stripped || undefined
  }
  if (!item || typeof item !== 'object' || !('msg' in item)) {
    return undefined
  }
  const { msg } = item as HttpValidationDetailItem
  if (typeof msg !== 'string') {
    return undefined
  }
  const stripped = stripHtmlFromErrorMessage(msg).trim()
  if (!stripped) {
    return undefined
  }
  if (options?.includeFieldLabels === false) {
    return stripped
  }
  const path = formatValidationDetailPath((item as HttpValidationDetailItem).loc)
  return path ? `${path}: ${stripped}` : stripped
}

export function formatHttpValidationDetails(
  detail: unknown,
  options?: { includeFieldLabels?: boolean },
): string[] {
  if (typeof detail === 'string') {
    const stripped = stripHtmlFromErrorMessage(detail).trim()
    return stripped ? [stripped] : []
  }
  if (!Array.isArray(detail)) {
    return []
  }
  return detail
    .map((item) => messageFromDetailItem(item, options))
    .filter((message): message is string => Boolean(message))
}

export function formatHttpValidationErrorMessage(
  detail: unknown,
  options?: { includeFieldLabels?: boolean },
): string | undefined {
  const messages = formatHttpValidationDetails(detail, options)
  return messages.length ? messages.join('\n') : undefined
}
